import React, { useEffect, useState, useRef } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import {
  Calendar,
  Home,
  MessageSquare,
  User,
  LogOut,
  MessageCircle
} from 'lucide-react';
import axios from 'axios';
import TrripLogo from '../../assets/Trrip_logo/trrip_logo.png';
import MobileNav from './DashboardComponents/MobileNav';
import ImageGallery from './DashboardComponents/ImageGallery';
import PartnerNavbar from '../../components/Dashboard/PartnerNavbar';

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('');
  const [userData, setUserData] = useState({
    business_name: '',
    owner_name: '',
    phone_number: '',
    email: '',
    address: '',
    city: '',
    pincode: '',
    description: '',
    businessHours: {
      days: [],
      openingTime: '',
      closingTime: '',
      openingTime2: '',
      closingTime2: ''
    },
    image_url: [],
    ambience_image_url: [],
    food_image_url: [],
    menu_image_url: [],
    beverage_image_url: [],
    logo_url: '',
    map_url: '',
    latitude: '',
    longitude: '',
    commission: 0,
    secondary_tags: [],
    primary_tags: []
  });
  const [galleryConfig, setGalleryConfig] = useState({ isOpen: false, images: [], index: 0 });
  const [walletBalance, setWalletBalance] = useState(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  const formDataRef = useRef({});
  const fileInputRef = useRef(null);
  const menuFileInputRef = useRef(null);
  const ambienceFileInputRef = useRef(null);
  const foodFileInputRef = useRef(null);
  const beverageFileInputRef = useRef(null);
  const logoInputRef = useRef(null);

  useEffect(() => {
    const path = location.pathname.split('/restaurant/')[1] || '';
    setActiveTab(path || 'home');
  }, [location]);

  const fetchUserData = async () => {
    let token = null;
    let id = null;
    const categories = ['restaurant', 'shop', 'activities'];
    for (const category of categories) {
      const storedToken = localStorage.getItem(`token_partner_${category}`);
      const storedId = localStorage.getItem(`id_partner_${category}`);
      if (storedToken && storedId) {
        token = storedToken;
        id = storedId;
        break;
      }
    }
    if (!token || !id) {
      navigate('/login');
      return;
    }
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/auth/profile/`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUserData(response.data);
      formDataRef.current = response.data;
      
      try {
        const tokenPayload = JSON.parse(atob(token.split('.')[1]));
        const partnerId = tokenPayload.business_id;
        const walletRes = await axios.get(`${process.env.REACT_APP_BASE_URL}/wallet/Restaurant/${partnerId}`);
        if (walletRes.data.success) {
          setWalletBalance(walletRes.data.balance);
        }
      } catch (err) {
        console.error('Error fetching wallet balance:', err);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  };

  useEffect(() => {
    fetchUserData();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token_partner_restaurant');
    localStorage.removeItem('id_partner_restaurant');
    localStorage.removeItem('roleid');
    navigate('/login');
  };

  const uploadImage = async (file) => {
    if (!file) return null;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await axios.post(
        'https://4trip-server.trrip.live/api/upload/single',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data.fileUrl;
    } catch (error) {
      toast.error('Failed to upload image');
      return null;
    }
  };

  const handleImageUpload = async (files) => {
    const uploadPromises = Array.from(files).map((file) => uploadImage(file));
    try {
      const urls = await Promise.all(uploadPromises);
      const newImageUrls = [
        ...formDataRef.current.image_url,
        ...urls.filter((url) => url !== null)
      ];
      formDataRef.current = { ...formDataRef.current, image_url: newImageUrls };
      setUserData((prev) => ({ ...prev, image_url: newImageUrls }));
    } catch (error) {
      toast.error('Failed to upload some images');
    }
  };

  const handleMenuImageUpload = async (files) => {
    const uploadPromises = Array.from(files).map((file) => uploadImage(file));
    try {
      const urls = await Promise.all(uploadPromises);
      const newMenuImageUrls = [
        ...(formDataRef.current.menu_image_url || []),
        ...urls.filter((url) => url !== null)
      ];
      formDataRef.current = { ...formDataRef.current, menu_image_url: newMenuImageUrls };
      setUserData((prev) => ({ ...prev, menu_image_url: newMenuImageUrls }));
    } catch (error) {
      toast.error('Failed to upload some menu images');
    }
  };

  const handleAmbienceImageUpload = async (files) => {
    const uploadPromises = Array.from(files).map((file) => uploadImage(file));
    try {
      const urls = await Promise.all(uploadPromises);
      const newUrls = [
        ...(formDataRef.current.ambience_image_url || []),
        ...urls.filter((url) => url !== null)
      ];
      formDataRef.current = { ...formDataRef.current, ambience_image_url: newUrls };
      setUserData((prev) => ({ ...prev, ambience_image_url: newUrls }));
    } catch (error) {
      toast.error('Failed to upload some ambience images');
    }
  };

  const handleFoodImageUpload = async (files) => {
    const uploadPromises = Array.from(files).map((file) => uploadImage(file));
    try {
      const urls = await Promise.all(uploadPromises);
      const newUrls = [
        ...(formDataRef.current.food_image_url || []),
        ...urls.filter((url) => url !== null)
      ];
      formDataRef.current = { ...formDataRef.current, food_image_url: newUrls };
      setUserData((prev) => ({ ...prev, food_image_url: newUrls }));
    } catch (error) {
      toast.error('Failed to upload some food images');
    }
  };

  const handleBeverageImageUpload = async (files) => {
    const uploadPromises = Array.from(files).map((file) => uploadImage(file));
    try {
      const urls = await Promise.all(uploadPromises);
      const newUrls = [
        ...(formDataRef.current.beverage_image_url || []),
        ...urls.filter((url) => url !== null)
      ];
      formDataRef.current = { ...formDataRef.current, beverage_image_url: newUrls };
      setUserData((prev) => ({ ...prev, beverage_image_url: newUrls }));
    } catch (error) {
      toast.error('Failed to upload some drinks menu images');
    }
  };

  const handleLogoUpload = async (file) => {
    const url = await uploadImage(file);
    if (url) {
      formDataRef.current = { ...formDataRef.current, logo_url: url };
      setUserData((prev) => ({ ...prev, logo_url: url }));
    }
  };

  const removeImage = (index) => {
    const updatedImages = [...(formDataRef.current.image_url || [])];
    updatedImages.splice(index, 1);
    formDataRef.current = { ...formDataRef.current, image_url: updatedImages };
    setUserData((prev) => ({ ...prev, image_url: updatedImages }));
  };

  const removeMenuImage = (index) => {
    const updatedMenuImages = [...(formDataRef.current.menu_image_url || [])];
    updatedMenuImages.splice(index, 1);
    formDataRef.current = { ...formDataRef.current, menu_image_url: updatedMenuImages };
    setUserData((prev) => ({ ...prev, menu_image_url: updatedMenuImages }));
  };

  const removeAmbienceImage = (index) => {
    const updated = [...(formDataRef.current.ambience_image_url || [])];
    updated.splice(index, 1);
    formDataRef.current = { ...formDataRef.current, ambience_image_url: updated };
    setUserData((prev) => ({ ...prev, ambience_image_url: updated }));
  };

  const removeFoodImage = (index) => {
    const updated = [...(formDataRef.current.food_image_url || [])];
    updated.splice(index, 1);
    formDataRef.current = { ...formDataRef.current, food_image_url: updated };
    setUserData((prev) => ({ ...prev, food_image_url: updated }));
  };

  const removeBeverageImage = (index) => {
    const updated = [...(formDataRef.current.beverage_image_url || [])];
    updated.splice(index, 1);
    formDataRef.current = { ...formDataRef.current, beverage_image_url: updated };
    setUserData((prev) => ({ ...prev, beverage_image_url: updated }));
  };

  const NavLink = ({ icon: Icon, label, value, path }) => (
    <button
      onClick={() => navigate(`/restaurant/${path}`)}
      className={`flex items-center w-full gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-all duration-300 group ${activeTab === value
        ? 'bg-[#067978] text-white shadow-lg shadow-teal-900/20'
        : 'text-gray-500 hover:bg-gray-50 hover:text-[#067978]'
        }`}
    >
      <Icon size={20} className={`${activeTab === value ? 'text-white' : 'text-gray-400 group-hover:text-[#067978]'} transition-colors`} />
      <span>{label}</span>
    </button>
  );

  return (
    <div className="h-screen w-screen overflow-hidden bg-slate-50 flex flex-col font-sans">
      <ToastContainer position="top-right" autoClose={3000} />
      
      {/* Modern Partner Header Navbar */}
      <PartnerNavbar
        portalType="restaurant"
        subtitle="Restaurant Management"
        userData={userData}
        walletBalance={walletBalance}
        onMenuClick={() => setIsMobileMenuOpen(true)}
      />

      <div className="flex flex-1 overflow-hidden">
        <aside className="hidden lg:flex w-64 bg-white border-r border-gray-100 flex-col flex-shrink-0 overflow-hidden select-none">
          <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
            <p className="text-[10px] font-black text-gray-300 uppercase tracking-[0.2em] mb-4 pl-4">Main Menu</p>
            <NavLink icon={Home} label="Dashboard" value="home" path="" />
            <NavLink icon={User} label="Restaurant Profile" value="profile" path="profile" />
            <NavLink icon={Calendar} label="Reservations" value="booking" path="booking" />
            <NavLink icon={MessageSquare} label="Guest Reviews" value="reviews" path="reviews" />
            <NavLink icon={MessageCircle} label="Support" value="support" path="support" />
          </nav>
          <div className="p-4 border-t border-gray-100 flex-shrink-0 bg-white">
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 text-sm font-bold text-rose-600 bg-rose-50 hover:bg-rose-100 rounded-xl transition-all group"
            >
              <LogOut size={18} className="text-rose-500 group-hover:scale-110 transition-transform" />
              <span>Logout</span>
            </button>
          </div>
        </aside>

        <MobileNav
          portalType="restaurant"
          subtitle="Restaurant Management"
          activeTab={activeTab}
          navigate={(path) => navigate(path ? `/restaurant/${path}` : '/restaurant')}
          userData={userData}
          handleLogout={handleLogout}
          isMenuOpen={isMobileMenuOpen}
          setIsMenuOpen={setIsMobileMenuOpen}
        />

        <div id="dashboard-scroll-container" className="flex-1 overflow-y-auto bg-[#F8FAFC]">
          <div className="max-w-[1920px] mx-auto min-h-full px-3 sm:px-6 lg:px-8 py-4 sm:py-8 pb-24 lg:pb-8">
            <Outlet 
              context={{ 
                userData, 
                setUserData, 
                setGalleryConfig, 
                formDataRef,
                logoInputRef,
                fileInputRef,
                menuFileInputRef,
                ambienceFileInputRef,
                foodFileInputRef,
                beverageFileInputRef,
                handleLogoUpload,
                handleImageUpload,
                handleMenuImageUpload,
                handleAmbienceImageUpload,
                handleFoodImageUpload,
                handleBeverageImageUpload,
                removeImage,
                removeMenuImage,
                removeAmbienceImage,
                removeFoodImage,
                removeBeverageImage
              }} 
            />
          </div>
        </div>
      </div>

      <ImageGallery
        isOpen={galleryConfig.isOpen}
        onClose={() => setGalleryConfig({ ...galleryConfig, isOpen: false })}
        images={galleryConfig.images}
        initialIndex={galleryConfig.index}
      />
    </div>
  );
};

export default Dashboard;
